import { useState, useRef } from 'react';
import {
  checkHotelCancellation,
  checkHotelValidation,
} from './checkHotelBookingValuationChanges';
import { HOTELS } from '@shared/helpers/routes';

export const useHotelBookingValuation = ({
  id_integration,
  id_provider,
  urlToken,
  id_transaction,
  hotelCode,
  id_hotel,
  idCity,
  initialPrice,
  initialCancelationFee,
  initialHotelsValidateResult,
}) => {
  // 🔹 Estados para UI (reactivos)
  const [price, setPrice] = useState(initialPrice ?? null);
  const [cancelationFee, setCancelationFee] = useState(
    initialCancelationFee ?? null
  );
  const [hotelsValidateResult, setHotelsValidateResult] = useState(
    initialHotelsValidateResult ?? null
  );

  const [valuationChanges, setValuationChanges] = useState(null);
  const [showValuationModal, setShowValuationModal] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [retryDismissed, setRetryDismissed] = useState(false);
  const [finalError, setFinalError] = useState(false);

  // 🔹 Refs para lógica inmediata (accesible sin esperar render)
  const priceRef = useRef(initialPrice ?? null);
  const cancelationFeeRef = useRef(initialCancelationFee ?? null);
  const acceptOnlyRef = useRef(false);

  const hotelsValidateResultRef = useRef(initialHotelsValidateResult ?? null);

  const handleValuationResponse = ({
    result,
    newPrice,
    changes,
    hasError,
    isValidation,
    acceptOnly = false,
  }) => {
    acceptOnlyRef.current = acceptOnly;

    if (hasError) {
      setRetryDismissed(false);
      setRetryCount(prev => {
        const next = prev + 1;
        if (next >= 3) {
          setFinalError(true);
        }
        return next;
      });
      return { ok: false, changes: null };
    }

    setRetryCount(0);
    setRetryDismissed(false);

    if (isValidation && result) {
      hotelsValidateResultRef.current = result;
      setHotelsValidateResult(result);
    }

    if ((changes?.deadline || !isValidation) && result) {
      cancelationFeeRef.current = result;
      setCancelationFee(result);
    }

    if (newPrice) {
      priceRef.current = newPrice;
      setPrice(newPrice);
    }

    if (changes) {
      setValuationChanges(changes);
      setShowValuationModal(true);
      return { ok: false, changes };
    }

    return { ok: true, changes: null };
  };

  const checkHotelValidationStep = async () => {
    const response = await checkHotelValidation({
      price: priceRef.current,
      id_transaction: id_transaction,
      previousValuation: hotelsValidateResultRef.current,
    });

    return handleValuationResponse({
      ...response,
      isValidation: true,
    });
  };

  const checkHotelCancellationStep = async () => {
    const response = await checkHotelCancellation({
      price: priceRef.current,
      id_transaction: id_transaction,
      previousValuation: cancelationFeeRef.current,
    });

    return handleValuationResponse({
      ...response,
      isValidation: false,
      acceptOnly: true,
    });
  };

  // 🔹 Función para reintento
  const retryCheck = checkHotelValidationStep;

  // 🔹 Cierra el modal de reintento sin volver a validar ni reiniciar el
  // contador de intentos; el usuario reintenta dándole de nuevo al botón de
  // reservar/confirmar. El contador sigue acumulando hasta los 3 intentos.
  const dismissRetry = () => setRetryDismissed(true);

  const showRetryModal =
    retryCount > 0 && retryCount < 3 && !finalError && !retryDismissed;

  // 🔹 Funciones de control de modales
  const closeModalAndGoBack = history => {
    setShowValuationModal(false);
    history.push(
      `${HOTELS}/detail?token=${urlToken}&integration=${id_integration}&hotel=${id_hotel}&hotel_code=${hotelCode}&city=${idCity}&id_provider=${id_provider}`
    );
  };

  const closeModalAndContinue = async callback => {
    setShowValuationModal(false);
    await callback?.();
  };

  const closeValuationModal = () => setShowValuationModal(false);

  return {
    // 🔹 Estados UI reactiva
    price,
    cancelationFee,
    valuationChanges,
    showValuationModal,
    retryCount,
    showRetryModal,
    finalError,
    hotelsValidateResult,

    // 🔹 Refs para lógica inmediata
    priceRef,
    cancelationFeeRef,
    hotelsValidateResultRef,
    acceptOnlyRef,

    // 🔹 Funciones
    checkHotelValidationStep,
    checkHotelCancellationStep,
    retryCheck,
    dismissRetry,
    closeModalAndGoBack,
    closeModalAndContinue,
    closeValuationModal,
  };
};
